import type { ActionPriority } from './actionPlan';

export const RISK_MATRIX_TYPES = ['BS_8800', 'AIHA_2015'] as const;
export type RiskMatrixType = typeof RISK_MATRIX_TYPES[number];

export const RISK_LEVELS = ['TRIVIAL', 'TOLERAVEL', 'MODERADO', 'SUBSTANCIAL', 'NAO_TOLERAVEL'] as const;
export type RiskLevel = typeof RISK_LEVELS[number];

export const RISK_MATRIX_TYPE_LABELS: Record<RiskMatrixType, string> = {
  BS_8800: 'BS 8800/2004 (4×3)',
  AIHA_2015: 'AIHA/2015 (5×5)',
};

export const RISK_LEVEL_LABELS: Record<RiskLevel, string> = {
  TRIVIAL: 'Trivial',
  TOLERAVEL: 'Tolerável',
  MODERADO: 'Moderado',
  SUBSTANCIAL: 'Substancial',
  NAO_TOLERAVEL: 'Não tolerável',
};

/** O que o nível diz sobre o risco, o que ele manda fazer, e com que urgência (#418). */
export interface RiskLevelDecision {
  /** Como o cliente classifica o risco: Irrelevante, De Atenção, Crítico, Intolerável. */
  classification: string;
  /** A tomada de decisão que o nível pede: Checar, Planejar, Fazer, Agir. */
  decision: string;
  priority: ActionPriority;
}

/**
 * Cópia manual de `backend/src/types/riskMatrix.ts` — as duas pontas recalculam o nível, e
 * `backend/tests/unit/riskMatrix.espelho.unit.test.ts` falha quando uma muda sem a outra.
 *
 * Cinco níveis viram quatro classificações: trivial e tolerável mandam a mesma coisa.
 */
export const RISK_LEVEL_DECISIONS: Record<RiskLevel, RiskLevelDecision> = {
  TRIVIAL: { classification: 'Irrelevante', decision: 'Checar', priority: 'IRRELEVANTE' },
  TOLERAVEL: { classification: 'Irrelevante', decision: 'Checar', priority: 'IRRELEVANTE' },
  MODERADO: { classification: 'De Atenção', decision: 'Planejar', priority: 'DE_ATENCAO' },
  SUBSTANCIAL: { classification: 'Crítico', decision: 'Fazer', priority: 'CRITICA' },
  NAO_TOLERAVEL: { classification: 'Intolerável', decision: 'Agir', priority: 'INTOLERAVEL' },
};

export const RISK_LEVEL_COLORS: Record<RiskLevel, string> = {
  TRIVIAL: '#4caf50',
  TOLERAVEL: '#8bc34a',
  MODERADO: '#ff9800',
  SUBSTANCIAL: '#f44336',
  NAO_TOLERAVEL: '#b71c1c',
};

export const BS_8800_PROBABILITY_LABELS: Record<number, string> = {
  1: 'Improvável',
  2: 'Inesperado',
  3: 'Provável',
  4: 'Altamente provável',
};

export const BS_8800_SEVERITY_LABELS: Record<number, string> = {
  1: 'Leve',
  2: 'Média',
  3: 'Severo',
};

export const AIHA_PROBABILITY_LABELS: Record<number, string> = {
  1: 'Baixa',
  2: 'Intermediária',
  3: 'Mediana',
  4: 'Alto',
  5: 'Altíssima',
};

export const AIHA_SEVERITY_LABELS: Record<number, string> = {
  1: 'Leve',
  2: 'Intermediária',
  3: 'Moderada',
  4: 'Grave',
  5: 'Gravíssima',
};

const BS_8800_MATRIX: RiskLevel[][] = [
  ['TRIVIAL', 'TOLERAVEL', 'MODERADO'],
  ['TOLERAVEL', 'MODERADO', 'SUBSTANCIAL'],
  ['MODERADO', 'SUBSTANCIAL', 'NAO_TOLERAVEL'],
  ['SUBSTANCIAL', 'NAO_TOLERAVEL', 'NAO_TOLERAVEL'],
];

const AIHA_MATRIX: RiskLevel[][] = [
  ['TRIVIAL', 'TRIVIAL', 'TOLERAVEL', 'TOLERAVEL', 'MODERADO'],
  ['TRIVIAL', 'TOLERAVEL', 'TOLERAVEL', 'MODERADO', 'SUBSTANCIAL'],
  ['TOLERAVEL', 'TOLERAVEL', 'MODERADO', 'SUBSTANCIAL', 'NAO_TOLERAVEL'],
  ['TOLERAVEL', 'MODERADO', 'SUBSTANCIAL', 'NAO_TOLERAVEL', 'NAO_TOLERAVEL'],
  ['MODERADO', 'SUBSTANCIAL', 'NAO_TOLERAVEL', 'NAO_TOLERAVEL', 'NAO_TOLERAVEL'],
];

export const RISK_LEVEL_SHORT_LABELS: Record<RiskLevel, string> = {
  TRIVIAL: 'Trivial',
  TOLERAVEL: 'Tolerável',
  MODERADO: 'Moderado',
  SUBSTANCIAL: 'Substancial',
  NAO_TOLERAVEL: 'Intolerável',
};

export function getRiskMatrix(matrixType: RiskMatrixType): RiskLevel[][] {
  return matrixType === 'BS_8800' ? BS_8800_MATRIX : AIHA_MATRIX;
}

export function calculateRiskLevel(
  matrixType: RiskMatrixType,
  probability: number,
  severity: number,
): RiskLevel | null {
  const matrix = getRiskMatrix(matrixType);
  const row = matrix[probability - 1];
  if (!row) return null;
  return row[severity - 1] ?? null;
}

export function getMaxProbability(matrixType: RiskMatrixType): number {
  return matrixType === 'BS_8800' ? 4 : 5;
}

export function getMaxSeverity(matrixType: RiskMatrixType): number {
  return matrixType === 'BS_8800' ? 3 : 5;
}

export function getProbabilityLabels(matrixType: RiskMatrixType): Record<number, string> {
  return matrixType === 'BS_8800' ? BS_8800_PROBABILITY_LABELS : AIHA_PROBABILITY_LABELS;
}

export function getSeverityLabels(matrixType: RiskMatrixType): Record<number, string> {
  return matrixType === 'BS_8800' ? BS_8800_SEVERITY_LABELS : AIHA_SEVERITY_LABELS;
}

export interface RiskMatrixTriple {
  riskMatrixType: RiskMatrixType | null;
  riskProbability: number | null;
  riskSeverity: number | null;
}

/**
 * Returns the matrix triple from a source only when it is COMPLETE
 * (type + probability + severity all present); otherwise returns all-null.
 * Prevents pre-filling a partial/unsatisfiable matrix into a form that
 * requires the full triple.
 */
export function pickCompleteMatrix(
  source:
    | {
        riskMatrixType?: RiskMatrixType | null;
        riskProbability?: number | null;
        riskSeverity?: number | null;
      }
    | null
    | undefined,
): RiskMatrixTriple {
  if (
    source != null &&
    source.riskMatrixType != null &&
    source.riskProbability != null &&
    source.riskSeverity != null
  ) {
    return {
      riskMatrixType: source.riskMatrixType,
      riskProbability: source.riskProbability,
      riskSeverity: source.riskSeverity,
    };
  }
  return { riskMatrixType: null, riskProbability: null, riskSeverity: null };
}
